// Ops bas niveau sur le store IndexedDB `userData` (donnees user synced).
// Le flag `dirty` tient lieu d'outbox : une ecriture locale pose dirty=true,
// le sync engine pousse les rows dirty puis les deflague. LWW sur updated_at.

// Ecriture locale d'une donnee user. Pose dirty=true et updated_at=maintenant
// (horloge device, fait foi pour le LWW). `updatedAt` injectable pour les tests.
export async function putUserData(db, { scope, item_key, data, deleted = false }, updatedAt) {
  const row = {
    scope,
    item_key,
    data,
    deleted,
    updated_at: updatedAt || new Date().toISOString(),
    dirty: true,
  };
  await db.put('userData', row);
  return row;
}

// Toutes les rows en attente de push (dirty=true). Pas d'index secondaire :
// le volume de donnees user est faible, un getAll + filter suffit.
export async function getDirty(db) {
  const all = await db.getAll('userData');
  return all.filter((r) => r.dirty);
}

// Deflague une row apres un push acquitte par le serveur. Idempotent : si la row
// a ete reecrite entre-temps (redevenue dirty), on ne l'ecrase pas.
export async function clearDirty(db, scope, item_key, pushedUpdatedAt) {
  const tx = db.transaction('userData', 'readwrite');
  const row = await tx.store.get([scope, item_key]);
  if (row && row.dirty && row.updated_at === pushedUpdatedAt) {
    row.dirty = false;
    await tx.store.put(row);
  }
  await tx.done;
}

// Applique un changement venu du serveur (pull) en LWW local : la version
// entrante gagne seulement si son updated_at est strictement plus recent que
// le local. La row appliquee n'est pas dirty (elle vient du serveur).
// Retourne true si elle a ete ecrite (le miroir memoire suit alors la meme
// decision, cf. userStore.applyServerRow).
export async function applyPulled(db, change) {
  const { scope, item_key, data, deleted, updated_at } = change;
  const tx = db.transaction('userData', 'readwrite');
  const local = await tx.store.get([scope, item_key]);
  const wins = !local || new Date(local.updated_at) < new Date(updated_at);
  if (wins) {
    await tx.store.put({ scope, item_key, data, deleted, updated_at, dirty: false });
  }
  await tx.done;
  return wins;
}

export async function getUserData(db, scope, item_key) {
  return db.get('userData', [scope, item_key]);
}

export async function getAllUserData(db) {
  return db.getAll('userData');
}

// Scopes qui portent le travail de la personne : ce qu'elle a sauve elle-meme.
// Les settings n'y sont pas : ils se reconstruisent, et une valeur ecrite par
// l'app elle-meme ferait passer un invite vierge pour un invite installe.
const CREATION_SCOPES = new Set(['pattern', 'preset', 'progression']);

// Y a-t-il quelque chose a perdre a une bascule d'identite ? Lu dans IndexedDB
// et pas dans le miroir memoire : au lancement A FROID par un lien, le handle
// de base arrive avant l'hydratation du miroir, qui repondrait alors "rien"
// pour un invite qui a dix patterns.
//
// Le demo-preset seede a l'install porte isDefault : personne ne l'a sauve, il
// ne compte pas.
export async function hasLocalCreations(db) {
  return (await countLocalCreations(db)) > 0;
}

// Meme filtre, en nombre : le dialogue de consentement du handoff (S4, F10)
// dit combien de sauvegardes restent sur le profil invite de l'appareil.
export async function countLocalCreations(db) {
  if (!db) return 0;
  const rows = await getAllUserData(db);
  return (rows || []).filter(
    (r) => !r.deleted && CREATION_SCOPES.has(r.scope) && !r.data?.isDefault
  ).length;
}

// Supprime physiquement toutes les rows des scopes donnes (reset user data).
// Suppression dure locale (pas un deleted logique) : utilise pour le bouton
// "Reset User Data" qui efface settings/achats en preservant les collections.
export async function clearScopes(db, scopes) {
  const set = new Set(scopes);
  const tx = db.transaction('userData', 'readwrite');
  let cursor = await tx.store.openCursor();
  while (cursor) {
    if (set.has(cursor.value.scope)) await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
}

// Purge dure de TOUTES les donnees user locales + curseur de pull. Utilise au
// changement d'identite (sign-in vers un compte existant, sign-out) : les rows
// locales appartiennent a l'ancien user et ne doivent ni rester affichees ni
// etre poussees sous le nouveau user_id. Le pull suivant repart de zero.
export async function clearAllUserData(db) {
  // Base indisponible (IndexedDB en échec au boot) : il n'y a rien à purger, mais
  // la déconnexion qui appelle cette fonction doit aboutir quand même.
  if (!db) return;
  await db.clear('userData');
  await db.delete('syncMeta', 'lastPull');
}

// Curseur de pull (timestamp serveur du dernier pull applique).
export async function getLastPull(db) {
  return db.get('syncMeta', 'lastPull');
}

export async function setLastPull(db, serverTime) {
  await db.put('syncMeta', serverTime, 'lastPull');
}

// Remet le curseur de pull a zero sans toucher aux donnees. Utilise par "Reset
// User Data" : les rows sont effacees localement, mais les rows dont le serveur
// est proprietaire (purchase/patreon) sont plus anciennes que le curseur et ne
// reviendraient donc dans aucun delta. Sans ce reset, l'utilisateur perdrait
// son acces Patreon jusqu'au prochain refresh.
export async function resetPullCursor(db) {
  await db.delete('syncMeta', 'lastPull');
}

// Drapeaux de sync (ex: migration localStorage faite). Stockes dans syncMeta.
export async function getSyncFlag(db, key) {
  return db.get('syncMeta', key);
}

export async function setSyncFlag(db, key, value) {
  await db.put('syncMeta', value, key);
}

// Ecriture LWW-gardee utilisee par la migration : n'ecrase une row existante
// que si la version entrante est strictement plus recente. Avec un timestamp
// historique fixe (epoch de migration), une migration qui rejoue n'ecrase
// jamais une donnee serveur deja tiree, ni une ecriture user posterieure.
// dirty=true pour que la row migree remonte au prochain push.
export async function migratePut(db, { scope, item_key, data, deleted = false }, updatedAt) {
  const tx = db.transaction('userData', 'readwrite');
  const existing = await tx.store.get([scope, item_key]);
  let written = false;
  if (!existing || new Date(existing.updated_at) < new Date(updatedAt)) {
    await tx.store.put({ scope, item_key, data, deleted, updated_at: updatedAt, dirty: true });
    written = true;
  }
  await tx.done;
  return written;
}
